/**
 * MetricSparkline — Mini-gráfica de las tarjetas KPI (área suave o barras).
 *
 * QUÉ HACE: dibuja la serie de un KPI con dos variantes:
 *   - `area`: línea quebrada (zigzag) con relleno degradado del color actual
 *     (`currentColor`) y un nodo en el punto actual/pico.
 *   - `bars`: barras verticales con la barra actual resaltada.
 *   Opcionalmente dibuja la fila de etiquetas del eje X (`showLabels`) y un eje Y
 *   de 3 guías punteadas (máximo, medio y $0) con sus etiquetas (`showAxis`).
 * QUÉ INVOCA: React (`useId`) y `formatChartAxisCop`.
 * QUÉ EXPORTA: MetricSparkline, MetricSparklinePoint.
 * QUÉ NO DEBE HACER: no calcula métricas, no accede a datos ni conoce el dominio.
 */
import { useId } from "react";
import type { CSSProperties } from "react";
import { useAnimatedSeries } from "@/hooks/useAnimatedSeries";
import { barStaggerDelay, MOTION } from "@/lib/animation/motion";
import { formatChartAxisCop } from "@/lib/utils/formatters";

/** Punto de la mini-gráfica de una tarjeta. */
export interface MetricSparklinePoint {
  /** Etiqueta corta del punto ("Hoy", "Lu", "8a"), para el eje X y el título. */
  label: string;
  /** Valor del punto: define la altura relativa de la curva/barra. */
  value: number;
  /** Monto formateado para el título nativo ("$12.3M"). */
  valueLabel?: string;
  /** Marca el punto actual/pico (nodo o barra resaltada y etiqueta en negrita). */
  isCurrent?: boolean;
}

export type MetricSparklineVariant = "area" | "bars";

interface MetricSparklineProps {
  points: MetricSparklinePoint[];
  /** Forma de la gráfica; por defecto `area`. */
  variant?: MetricSparklineVariant;
  /** Dibuja la fila de etiquetas del eje X bajo la gráfica. */
  showLabels?: boolean;
  /** Dibuja el eje Y de 3 guías punteadas (máximo, medio, $0) con etiquetas. */
  showAxis?: boolean;
  className?: string;
}

/** Lienzo interno del SVG de área (se estira con `preserveAspectRatio="none"`). */
const VIEW_W = 100;
const VIEW_H = 40;
/** Margen vertical para que el pico y el valle no toquen los bordes. */
const PAD_Y = 5;
/** Margen horizontal: 0 para que la línea/área llegue a los bordes a cualquier
 *  ancho (con `preserveAspectRatio="none"` un margen en unidades del viewBox se
 *  escala horizontalmente y deja un hueco visible en pantallas anchas). */
const PAD_X = 0;

const toCoords = (
  points: MetricSparklinePoint[],
): { x: number; y: number }[] => {
  const values = points.map((point) => point.value);
  const max = Math.max(...values);
  const min = Math.min(...values);
  const range = max - min;
  const stepX =
    points.length > 1 ? (VIEW_W - PAD_X * 2) / (points.length - 1) : 0;
  return points.map((point, index) => {
    const norm = range > 0 ? (point.value - min) / range : 0.5;
    return {
      x: PAD_X + index * stepX,
      y: VIEW_H - PAD_Y - norm * (VIEW_H - PAD_Y * 2),
    };
  });
};

/** Línea quebrada (zigzag): segmentos rectos entre puntos consecutivos. */
const zigzagPath = (coords: { x: number; y: number }[]): string => {
  if (coords.length === 0) return "";
  if (coords.length === 1) return `M ${coords[0].x} ${coords[0].y}`;
  return coords
    .map((point, index) =>
      index === 0 ? `M ${point.x} ${point.y}` : `L ${point.x} ${point.y}`,
    )
    .join(" ");
};

const titleFor = (point: MetricSparklinePoint): string =>
  `${point.label}${point.valueLabel ? ` · ${point.valueLabel}` : ""}`;

/** Variante de área: línea quebrada con relleno degradado y nodo del pico. */
const AreaSparkline = ({ points }: { points: MetricSparklinePoint[] }) => {
  const gradientId = useId().replace(/:/g, "");
  const coords = toCoords(points);
  const linePath = zigzagPath(coords);
  const first = coords[0];
  const last = coords[coords.length - 1];
  const areaPath = `${linePath} L ${last.x} ${VIEW_H} L ${first.x} ${VIEW_H} Z`;

  const highlightIndex = (() => {
    const current = points.findIndex((point) => point.isCurrent);
    if (current >= 0) return current;
    return points.reduce(
      (best, point, index) => (point.value > points[best].value ? index : best),
      0,
    );
  })();
  const highlight = coords[highlightIndex];
  const highlightPoint = points[highlightIndex];

  return (
    <div className="relative h-full w-full">
      <svg
        viewBox={`0 0 ${VIEW_W} ${VIEW_H}`}
        preserveAspectRatio="none"
        width="100%"
        height="100%"
        className="absolute inset-0 h-full w-full"
      >
        <defs>
          <linearGradient id={gradientId} x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor="currentColor" stopOpacity="0.35" />
            <stop offset="100%" stopColor="currentColor" stopOpacity="0" />
          </linearGradient>
        </defs>
        <path
          d={areaPath}
          fill={`url(#${gradientId})`}
          stroke="none"
          className="animate-chart-fade"
          style={{ animationDelay: "220ms" }}
        />
        <path
          d={linePath}
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
          vectorEffect="non-scaling-stroke"
          className="animate-chart-fade"
        />
      </svg>
      {highlightPoint && (
        <span
          title={titleFor(highlightPoint)}
          className="absolute h-1.5 w-1.5 -translate-x-1/2 -translate-y-1/2 rounded-full bg-current ring-2 ring-white dark:ring-slate-800"
          style={{
            left: `clamp(3px, ${(highlight.x / VIEW_W) * 100}%, calc(100% - 3px))`,
            top: `${(highlight.y / VIEW_H) * 100}%`,
          }}
        />
      )}
    </div>
  );
};

/**
 * Variante de barras: hereda el color de la tarjeta (`currentColor`); la barra
 * actual va sólida y las demás atenuadas.
 */
const BarsSparkline = ({ points }: { points: MetricSparklinePoint[] }) => {
  const max = points.reduce((top, point) => Math.max(top, point.value), 0);
  return (
    <div className="flex h-full w-full items-end gap-[3px]">
      {points.map((point, index) => {
        const ratio = max > 0 ? point.value / max : 0;
        return (
          <div
            key={`${point.label}-${index}`}
            title={titleFor(point)}
            className="relative h-full flex-1 overflow-hidden rounded-t-[3px]"
          >
            <div
              className={`absolute inset-x-0 bottom-0 origin-bottom rounded-t-[3px] bg-current animate-chart-rise ${
                point.isCurrent ? "" : "opacity-30"
              }`}
              style={
                {
                  height: `${Math.max(6, Math.round(ratio * 100))}%`,
                  animationDelay: barStaggerDelay(index),
                } as CSSProperties
              }
            />
          </div>
        );
      })}
    </div>
  );
};

/** Fila de etiquetas del eje X (el punto actual hereda el color de la tarjeta). */
const AxisLabels = ({
  points,
  className = "",
}: {
  points: MetricSparklinePoint[];
  className?: string;
}) => (
  <div className={`mt-1 flex gap-[2px] ${className}`}>
    {points.map((point, index) => (
      <span
        key={`${point.label}-${index}`}
        className={`min-w-0 flex-1 whitespace-nowrap text-center text-[5px] leading-tight sm:text-[7px] ${
          point.isCurrent
            ? "font-semibold"
            : "text-gray-400 dark:text-slate-500"
        }`}
      >
        {point.label}
      </span>
    ))}
  </div>
);

/** Eje Y: etiquetas de máximo, medio y $0 en un gutter fijo a la izquierda. */
const SparklineAxisLabels = ({ max }: { max: number }) => (
  <div className="relative w-8 shrink-0 pr-1.5 text-right text-[7px] font-normal tabular-nums text-slate-400 sm:w-9 sm:pr-2 sm:text-[8px] dark:text-slate-500">
    <span className="absolute right-1.5 top-0 sm:right-2">
      {formatChartAxisCop(max)}
    </span>
    <span className="absolute right-1.5 top-1/2 -translate-y-1/2 sm:right-2">
      {formatChartAxisCop(max / 2)}
    </span>
    <span className="absolute bottom-0 right-1.5 sm:right-2">
      {formatChartAxisCop(0)}
    </span>
  </div>
);

export const MetricSparkline = ({
  points,
  variant = "area",
  showLabels = false,
  showAxis = false,
  className = "",
}: MetricSparklineProps) => {
  const animatedPoints = useAnimatedSeries(points, {
    keys: ["value"],
    duration: MOTION.barRise,
    stagger: MOTION.barRise,
  });

  /**
   * Sin serie suficiente (menos de 2 puntos) o todos los valores en cero: en
   * vez de dejar el hueco vacío o pintar barras mínimas "fantasma", se muestra
   * una **línea base punteada** que comunica "sin datos" sin romper el layout
   * de la tarjeta KPI. Se conservan el eje Y y la fila de días cuando aplican.
   */
  const hasValues =
    points.length >= 2 && points.some((point) => point.value !== 0);
  if (!hasValues) {
    return (
      <div
        className={`flex h-full w-full flex-col ${className}`}
        aria-hidden="true"
      >
        <div className="flex min-h-0 flex-1">
          {showAxis && <SparklineAxisLabels max={0} />}
          <div className="relative min-h-0 min-w-0 flex-1">
            <div className="absolute inset-x-0 bottom-0 border-t border-dashed border-gray-300 dark:border-slate-600" />
          </div>
        </div>
        {showLabels && (
          <AxisLabels
            points={points}
            className={showAxis ? "pl-8 sm:pl-9" : ""}
          />
        )}
      </div>
    );
  }

  const max = animatedPoints.reduce(
    (top, point) => Math.max(top, point.value),
    0,
  );

  return (
    <div
      className={`flex h-full w-full flex-col ${className}`}
      aria-hidden="true"
    >
      <div className="flex min-h-0 flex-1">
        {showAxis && <SparklineAxisLabels max={max} />}
        <div className="relative min-h-0 min-w-0 flex-1">
          {showAxis && (
            <div className="pointer-events-none absolute inset-0 z-10">
              <div className="absolute inset-x-0 top-0 border-t border-dashed border-slate-200 dark:border-slate-700" />
              <div className="absolute inset-x-0 top-1/2 border-t border-dashed border-slate-200 dark:border-slate-700" />
              <div className="absolute inset-x-0 bottom-0 border-t border-dashed border-slate-200 dark:border-slate-700" />
            </div>
          )}
          {variant === "bars" ? (
            <BarsSparkline points={animatedPoints} />
          ) : (
            <AreaSparkline points={animatedPoints} />
          )}
        </div>
      </div>
      {showLabels && (
        <AxisLabels
          points={points}
          className={showAxis ? "pl-8 sm:pl-9" : ""}
        />
      )}
    </div>
  );
};
